/**
 * 校徽订购 - 家长填写字段预设（后台只选类型，Key/规则由程序生成）。最后更新：2026-07-14
 */

/** 常用字段预设列表（Key 固定，后台只选类型） */
export const collectFieldPresets = [
  { key: 'schoolName', label: '学校名称', fieldType: 'text' },
  { key: 'studentName', label: '学生姓名', fieldType: 'text' },
  { key: 'gradeName', label: '就读年级', fieldType: 'text' },
  { key: 'className', label: '就读班级', fieldType: 'text' },
  { key: 'studentNo', label: '学号', fieldType: 'text' },
  { key: 'parentPhone', label: '联系方式', fieldType: 'text' },
  { key: 'guardians', label: '监护人信息', fieldType: 'guardians' },
  { key: 'badgeNo', label: '校徽编号', fieldType: 'text' },
  { key: 'photo', label: '学生证件照', fieldType: 'image' }
]

/** 按 FieldKey 查找预设项 */
export function getPresetByKey(key) {
  return collectFieldPresets.find(p => p.key === key)
}

/** 获取预设字段中文标签，未命中时返回 key 本身 */
export function getPresetLabel(key) {
  const preset = getPresetByKey(key)
  return preset ? preset.label : key
}

/** 字段类型中文说明（管理端展示用） */
export function getFieldTypeLabel(fieldType) {
  const map = {
    text: '单行文字',
    image: '图片上传',
    guardians: '监护人组'
  }
  return map[fieldType] || fieldType
}

/** 管理端列表副标题（简短说明，与下方 hint 对应） */
export const collectFieldAdminRemarks = {
  schoolName: '就读学校全称',
  studentName: '学生真实姓名',
  gradeName: '当前就读年级',
  className: '就读班级名称',
  studentNo: '家长填写学号，默认直接印在校徽上',
  parentPhone: '家长手机号',
  guardians: '可重复填写：姓名、电话、关系'
}

/** 从 RuleJson 读取 hint / spec 文案 */
export function parseRuleJsonHint(ruleJson) {
  if (!ruleJson) return ''
  try {
    const obj = JSON.parse(ruleJson)
    return (obj.hint || obj.spec || '').trim()
  } catch (e) {
    return ''
  }
}

/** 收集字段管理端副标题 */
export function getCollectFieldAdminRemark(row) {
  if (!row || !row.FieldKey) return '—'
  const key = row.FieldKey
  if (key === 'badgeNo') {
    const ex = parseFieldExtras(key, row.RuleJson, row.AllowParentFill)
    return ex.allowParentFill ? '家长直接填写校徽编号' : '未手填时取学号作为校徽编号'
  }
  if (key === 'studentNo') {
    return '家长自行填写学号'
  }
  if (key === 'photo') {
    const ex = parseFieldExtras(key, row.RuleJson)
    return ex.photoSpec || '作为校徽头像，请上传1寸、白底标准证件照'
  }
  if (key === 'guardians') {
    return collectFieldAdminRemarks.guardians
  }
  if (collectFieldAdminRemarks[key]) return collectFieldAdminRemarks[key]
  const hint = parseRuleJsonHint(row.RuleJson)
  return hint || '—'
}

/** 从 RuleJson 解析出界面可编辑项 */
export function parseFieldExtras(fieldKey, ruleJson, allowParentFill) {
  const extras = { allowParentFill: !!allowParentFill, photoSpec: '' }
  if (!ruleJson) return extras
  try {
    const obj = JSON.parse(ruleJson)
    if (fieldKey === 'badgeNo') {
      extras.allowParentFill = !!obj.allowParentFill
    }
    if (fieldKey === 'photo' && obj.spec) {
      extras.photoSpec = obj.spec
    }
  } catch (e) { /* ignore */ }
  return extras
}

/** 根据预设与选项生成 RuleJson（仅编号、证件照需要） */
export function buildRuleJson(fieldKey, extras) {
  if (fieldKey === 'badgeNo') {
    return JSON.stringify({
      numberingRule: 'studentNo',
      allowParentFill: !!extras.allowParentFill
    })
  }
  if (fieldKey === 'photo') {
    return JSON.stringify({
      spec: extras.photoSpec || '作为校徽头像，请上传1寸、白底标准证件照'
    })
  }
  if (fieldKey === 'parentPhone') {
    return JSON.stringify({
      hint: '请输入家长手机号，便于联系与查询订单'
    })
  }
  if (fieldKey === 'guardians') {
    return JSON.stringify({
      hint: '请填写监护人信息，可添加多人',
      relationOptions: ['父亲', '母亲', '爷爷', '奶奶', '外公', '外婆', '家长', '其他']
    })
  }
  if (fieldKey === 'schoolName') {
    return JSON.stringify({
      hint: '请填写学生就读学校全称，将用于订单与校徽信息'
    })
  }
  if (fieldKey === 'gradeName') {
    return JSON.stringify({
      hint: '请选择或填写学生当前就读年级'
    })
  }
  if (fieldKey === 'studentName') {
    return JSON.stringify({
      hint: '请填写学生真实姓名，将用于校徽制作'
    })
  }
  if (fieldKey === 'className') {
    return JSON.stringify({
      hint: '请填写或选择就读班级'
    })
  }
  if (fieldKey === 'studentNo') {
    return JSON.stringify({
      hint: '请填写学生学号，将印在校徽上'
    })
  }
  return ''
}

/** 预设字段默认 SortNo（与 collectFieldPresets 顺序一致） */
export function getDefaultCollectFieldSortNo(fieldKey) {
  const index = collectFieldPresets.findIndex(p => p.key === fieldKey)
  return index >= 0 ? index + 1 : null
}

/** 当前列表相对默认配置是否需同步（序号、必填、类型、规则等） */
export function needsDefaultCollectFieldsSync(fields) {
  const defaults = getDefaultCollectFields()
  const map = {}
  ;(fields || []).forEach(f => {
    map[f.FieldKey] = f
  })
  return defaults.some(def => {
    const row = map[def.FieldKey]
    if (!row) return false
    if (row.SortNo !== def.SortNo) return true
    if (row.Title !== def.Title) return true
    if (row.FieldType !== def.FieldType) return true
    if (!!row.Required !== !!def.Required) return true
    if (!!row.AllowParentFill !== !!def.AllowParentFill) return true
    if (!!row.Enabled !== !!def.Enabled) return true
    if ((row.RuleJson || '') !== (def.RuleJson || '')) return true
    return false
  })
}

/** 一键初始化用的默认字段列表 */
export function getDefaultCollectFields() {
  return collectFieldPresets.map((p, i) => ({
    SortNo: i + 1,
    FieldKey: p.key,
    Title: p.label,
    FieldType: p.fieldType,
    Required: p.key !== 'guardians',
    AllowParentFill: false,
    RuleJson: buildRuleJson(p.key, {
      allowParentFill: false,
      photoSpec: '作为校徽头像，请上传1寸、白底标准证件照'
    }),
    Enabled: true
  }))
}
